import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'

import api from '../../../common/api.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {shipperOptionsSelector, setShipper} from '../../../data/shippers.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import {
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
} from '../../../store.js'
import {
  DragAndDropList,
  Draggable,
  DragHandle,
  MOVE_AFTER,
  MOVE_BEFORE,
} from '../../../common/components/DragAndDrop.js'
import className from '../../../common/className.js'
import Icon from '../../../common/icons/Icon.js'

export const MODAL_FORM = 'shipper_sort_modal'

export function showShipperSortModal() {
  const listOptions = shipperOptionsSelector(getState())

  setForm(MODAL_FORM, {
    listOptions,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

async function onShipperMove(values, nearValue, where) {
  const [value] = values
  const {listOptions: originalListOptions} = modalFormSelector(getState())

  // get the option
  const option = originalListOptions.find((option) => option.value === value)

  // get sorted items and exclude moving item
  const listOptions = originalListOptions.filter(
    (option) => option.value !== value,
  )

  // get index of near item
  const nearIndex = listOptions.findIndex(
    (option) => option.value === nearValue,
  )

  // put moving item in it's place
  if (where === MOVE_BEFORE) {
    listOptions.splice(nearIndex, 0, option)
  } else if (where === MOVE_AFTER) {
    listOptions.splice(nearIndex + 1, 0, option)
  }

  if (!isEqual(listOptions, originalListOptions)) {
    updateModalForm({listOptions})
  }
}

export async function saveShipperSort() {
  try {
    const {listOptions} = modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const shippers = await Promise.all(
      listOptions.map(async (option, index) => {
        const {json} = await api.put(`/shipper/${option.value}`, {index})

        return json
      }),
    )

    for (const shipper of shippers) {
      setShipper(shipper)
    }

    closeModal()

    showMessageToast('Carrier Display Order Updated')
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function ShipperSortModal({form}) {
  return (
    <ConfirmModal
      title="Arrange Carrier Order"
      modalSize="sm"
      onConfirm={() => saveShipperSort()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      error={form.serverError}
    >
      <DragAndDropList onMove={onShipperMove}>
        <p className="fs-01 lh-md margin-bottom-25">
          <strong className="inline-block v-align-middle">
            Reorder your carriers by dragging the{' '}
            <span className="inline-block margin-left-3 margin-right-3 v-align-middle op-30">
              <Icon
                name="drag-handle"
                className="i-drag-handle block fs-03-04 lh-sm"
              />
            </span>{' '}
            icon.
          </strong>
        </p>
        <div className="wrap--modal-draggable-items">
          {form.listOptions.map((option, index) => (
            <Draggable
              className="flex wrap--draggable-item"
              key={option.value}
              id={option.value}
              uiIndex={index}
            >
              <DragHandle className="padding-top-10 padding-bottom-10 border-top--light" />
              <div className="fs-01 flex--justify-col border-top--light padding-top-10 padding-bottom-10 flex-grow">
                <span
                  className={className`label--selectable__text label__carrier-logo fs-n1 lh-sm unbold flex align-items-center ${option.entity.vendor}`}
                >
                  <span className="lh-sm-md">{option.display}</span>
                </span>
              </div>
            </Draggable>
          ))}
        </div>
      </DragAndDropList>
    </ConfirmModal>
  )
}

export default onlyIfForm(ShipperSortModal, modalFormSelector)
